export type Lang = "de" | "en";

const de = {
  nav: {
    home: "Start",
    science: "Wissenschaft",
    studies: "Studien",
    calculator: "Rechner",
    safety: "Sicherheit",
    faq: "FAQ",
  },

  common: {
    languageLabel: "Sprache",
    themeToggle: "Farbschema umschalten",
  },
  footer: {
    tagline: "Unabhängiges Informationsportal zum Triple-Agonisten Retatrutid.",
    disclaimer:
      "Diese Website dient rein informativen Zwecken und ersetzt keine ärztliche Beratung. Stand: 2026.",
    linkScience: "Wirkmechanismus",
    linkStudies: "Klinische Studien",
    linkCalc: "Gewichtsverlust-Rechner",
    linkSafety: "Sicherheit & Recht",
  },
  home: {
    badge: "Ärztlich begleitet · Partnerklinik Bangkok",
    h1a: "Retatrutid-Behandlung in",
    h1em: "Thailand",
    h1b: "– ärztlich begleitet, legal, persönlich.",
    lead: "Wir vermitteln Ihnen den Zugang zur neuen GLP-1/GIP/Glucagon-Generation über eine Partnerklinik in Bangkok mit behördlicher Studien­genehmigung – inklusive Voruntersuchung, Dosis­eskalation und deutschsprachiger Betreuung von A bis Z.",
    ctaConsult: "Kostenloses Beratungsgespräch",
    ctaHow: "So funktioniert's",
    ctaData: "Studiendaten ansehen",
    ctaCalc: "Gewichtsverlust berechnen",
    intro: {
      eyebrow: "Einleitung",
      title: "Retatrutid: Das Peptid, über das aktuell alle sprechen",
      paragraphs: [
        "Retatrutid, auch bekannt als Reta Peptid oder LY3437943, gehört zu den spannendsten neuen Wirkstoffen im Bereich Gewichtsmanagement, Stoffwechsel und moderne Peptidforschung. Besonders interessant ist Retatrutid, weil es nicht nur auf einen, sondern gleich auf drei wichtige Signalwege im Körper wirkt: GLP-1, GIP und Glucagon. Deshalb wird es auch als Triple-Agonist bezeichnet.",
        "Viele Menschen interessieren sich für Retatrutid, weil es in Studien mit starkem Gewichtsverlust, besserer Appetitkontrolle und positiven Effekten auf den Stoffwechsel in Verbindung gebracht wird. Während bekannte Wirkstoffe wie Semaglutid oder Tirzepatid bereits viel Aufmerksamkeit bekommen haben, gilt Retatrutid für viele als möglicher nächster großer Schritt im Bereich moderner Abnehm- und Stoffwechseltherapien.",
        "Der besondere Ansatz von Retatrutid liegt darin, dass es mehrere Prozesse gleichzeitig beeinflussen kann. Es kann die Sättigung unterstützen, das Hungergefühl reduzieren und den Körper dabei beeinflussen, Energie anders zu verwerten. Genau deshalb suchen viele nach Begriffen wie Retatrutid Wirkung, Reta Peptid, Retatrutid Gewichtsverlust, Retatrutid Abnehmen, Retatrutid Erfahrungen, Retatrutid vs Tirzepatid oder Retatrutid vs Semaglutid.",
      ],
    },
    trustRow: {
      a: "Partnerklinik mit Studien­genehmigung",
      b: "Persönliche Betreuung DE / EN",
      c: "Ärztliche Aufsicht & Monitoring",
    },
    how: {
      eyebrow: "Ablauf",
      title: "So funktioniert Ihre Behandlung",
      lead: "Vier klare Schritte – von der ersten Anfrage bis zur laufenden Begleitung.",
      steps: [
        {
          title: "1 · Kostenlose Beratung",
          body: "Unverbindliches Erstgespräch (Video oder Telefon) auf Deutsch oder Englisch. Wir klären Ihre Fragen und den möglichen Behandlungspfad.",
        },
        {
          title: "2 · Ärztliche Eignungsprüfung",
          body: "Anamnese, Laborwerte und BMI-Bewertung durch das Ärzteteam der Partnerklinik. Sie erhalten eine klare Ja/Nein-Empfehlung.",
        },
        {
          title: "3 · Behandlung in Bangkok",
          body: "Start der Therapie in der Klinik: Wirkstoffgabe im Studien­protokoll, kontrollierte Dosis­eskalation, persönliche Einweisung.",
        },
        {
          title: "4 · Laufende Begleitung",
          body: "Regelmäßiges Monitoring, Nach­lieferungen, Rücksprachen mit dem Arzt – über die gesamte Behandlungs­dauer.",
        },
      ],
    },
    included: {
      eyebrow: "Leistungsumfang",
      title: "Was in Ihrer Behandlung enthalten ist",
      lead: "Ein Paket, das alle medizinisch relevanten Schritte abdeckt – transparent aufgeführt.",
      items: [
        "Ärztliche Voruntersuchung inkl. Labor",
        "Wirkstoff im genehmigten Studien­protokoll",
        "Individuelle Dosis­eskalation",
        "Regelmäßige Arzttermine & Monitoring",
        "Deutsch- und englischsprachige Betreuung",
        "Digitale Verlaufs­dokumentation",
        "Notfall-Ansprechpartner in Bangkok",
        "Optionaler Reise- und Aufenthalts-Support",
      ],
      priceNote: "Preis auf Anfrage – individuell nach Behandlungs­dauer und Dosisplan.",
    },
    results: {
      eyebrow: "Erwartbare Ergebnisse",
      title: "Bis zu −24 % Körpergewicht",
      lead: "In den TRIUMPH-Phase-II-Studien erreichten Patient:innen unter Retatrutid im Mittel rund 24 % Gewichtsverlust nach 48 Wochen – deutlich mehr als unter Semaglutid oder Tirzepatid.",
      metricLabel: "Ø Gewichtsverlust nach 48 Wochen (Phase II)",
      disclaimer:
        "Individuelle Ergebnisse variieren. Die Angaben stammen aus klinischen Studien und ersetzen keine ärztliche Beratung.",
      ctaStudies: "Studiendaten im Detail",
      ctaCalc: "Ihren Verlauf berechnen",
    },
    why: {
      eyebrow: "Warum Retatrutid Thailand",
      title: "Ein Angebot, dem Sie vertrauen können",
      cards: [
        {
          title: "Legaler Rahmen",
          body: "Behandlung ausschließlich über die genehmigte Studien­struktur unserer Partner­klinik in Bangkok – kein Grau- oder Schwarzmarkt.",
        },
        {
          title: "Ärztliche Qualität",
          body: "Standardisiertes Protokoll, kontrollierte Dosis­eskalation und kontinuierliches Monitoring durch Fachärzt:innen.",
        },
        {
          title: "Deutschsprachig End-to-End",
          body: "Von der ersten Anfrage über die Behandlung bis zur Nachsorge – persönlicher Kontakt in Ihrer Sprache.",
        },
      ],
    },
    scienceIntro: {
      eyebrow: "Wissenschaft & Sicherheit",
      title: "Fundiert. Transparent. Nachvollziehbar.",
      lead: "Wir arbeiten ausschließlich auf Basis publizierter Studien­daten und offener Kommunikation. Hier die Kurzfassung – detaillierte Belege auf den Unterseiten.",
    },
    tiles: {
      evoEyebrow: "Evolution",
      evoTitle: "Die nächste Evolution",
      evoBody: "Triple-Agonist: GLP-1, GIP und Glucagon gleichzeitig aktiviert.",
      triumphEyebrow: "TRIUMPH",
      triumphTitle: "Klinische Studienergebnisse",
      triumphBody: "24 % durchschnittlicher Gewichtsverlust nach 48 Wochen.",
      bgEyebrow: "Biochemie",
      bgTitle: "Wissenschaftlicher Hintergrund",
      bgBody: "Wie drei Rezeptoren den Stoffwechsel neu programmieren.",
      safeEyebrow: "Recht & Risiko",
      safeTitle: "Sicherheit & Legalität",
      safeBody: "Regulatorischer Status 2026 und Warnung vor Schwarzmarkt.",
      benchEyebrow: "Benchmark",
      benchCaption:
        "Durchschnittlicher Gewichtsverlust nach 48 Wochen unter Retatrutid in Phase II. Referenz: Semaglutid ~15 %, Tirzepatid ~22 %.",
      genEyebrow: "Generationen",
    },
    faqTeaser: {
      eyebrow: "Häufige Fragen",
      title: "Kurz beantwortet",
      cta: "Alle Fragen ansehen",
      items: [
        {
          q: "Ist die Behandlung legal?",
          a: "Ja. Sie erfolgt in einer Partner­klinik in Bangkok innerhalb eines von den thailändischen Behörden genehmigten Studien­rahmens.",
        },
        {
          q: "Muss ich nach Thailand reisen?",
          a: "Für Beginn und Einweisung ja. Nachfolgende Termine erfolgen teilweise remote – wir stimmen den Plan individuell mit Ihnen ab.",
        },
        {
          q: "Was kostet die Behandlung?",
          a: "Der Preis richtet sich nach Behandlungs­dauer und Dosisplan. Sie erhalten im Beratungs­gespräch ein transparentes Angebot.",
        },
        {
          q: "Wie schnell sehe ich Ergebnisse?",
          a: "Erste Effekte sind meist innerhalb weniger Wochen spürbar. Die Studien­daten beziehen sich auf 48 Wochen Behandlungs­dauer.",
        },
      ],
    },
    finalCta: {
      title: "Bereit für ein unverbindliches Beratungs­gespräch?",
      lead: "Wir melden uns innerhalb von 24 Stunden bei Ihnen – persönlich, diskret und ohne Verkaufs­druck.",
      reassure: "Kostenlos · Unverbindlich · Vertraulich",
      cta: "Jetzt Beratung anfragen",
    },
  },
  science: {
    eyebrow: "Wirkmechanismus",
    h1: "Drei Rezeptoren. Ein Molekül. Ein neuer Stoffwechsel.",
    lead: "Retatrutid ist der erste synthetische Peptid-Wirkstoff, der drei zentrale Stoffwechsel-Rezeptoren gleichzeitig moduliert. Jeder trägt einen distinkten Beitrag – die Kombination ist mehr als die Summe ihrer Teile.",
    combo: {
      title: "Warum die Kombination entscheidend ist",
      body: "Mono-Agonisten wie Semaglutid adressieren ausschließlich das Sättigungssignal. Dual-Agonisten wie Tirzepatid ergänzen den Insulinstoffwechsel. Retatrutid setzt zusätzlich am Grundumsatz an – die metabolische Kompensation, mit der der Körper sonst jede Diät sabotiert, wird unterbrochen.",
    },
    m: [
      {
        title: "GLP-1-Rezeptor",
        lead: "Sättigung im Gehirn, verzögerte Magenentleerung.",
        body: "Bindet an Rezeptoren im Hypothalamus und verstärkt zentrale Sättigungssignale. Gleichzeitig verlangsamt sich die Magenpassage – die Portionen werden kleiner, das Sättigungsgefühl bleibt länger bestehen.",
      },
      {
        title: "GIP-Rezeptor",
        lead: "Insulinsensitivität und Fettstoffwechsel.",
        body: "Verbessert die Insulinsensitivität des peripheren Gewebes und moduliert die Lipolyse. Fettdepots werden effizienter mobilisiert, postprandiale Glukosespitzen reduziert.",
      },
      {
        title: "Glucagon-Rezeptor",
        lead: "Direkte Steigerung des Grundumsatzes.",
        body: "Das entscheidende Novum: die Aktivierung des Glucagon-Rezeptors erhöht die basale Energieverbrennung. Der Körper verbrennt in Ruhe mehr Kalorien – unabhängig von Bewegung oder Diät.",
      },
    ],
  },
  studies: {
    eyebrow: "TRIUMPH-Programm",
    h1: "Die Daten hinter dem Paradigmenwechsel.",
    lead: "Phase-II- und Phase-III-Studien belegen einen bislang unerreichten Wirkgrad in der medikamentösen Adipositastherapie.",
    headline: "Headline-Ergebnis",
    headlineCaption: "Durchschnittlicher Gewichtsverlust nach 48 Wochen Behandlung.",
    duration: "Behandlungsdauer Phase II",
    phase: "Laufende TRIUMPH-Studien",
    compareTitle: "Drei Generationen im Vergleich",
    compareLead: "Wirkstoff, Mechanismus, Effektstärke und regulatorischer Status.",
    th: {
      gen: "Generation",
      drug: "Wirkstoff",
      type: "Typ",
      loss: "Gewichtsverlust",
      status: "Status Thailand",
    },
    approved: "In Thailand FDA-zugelassen",
    notApproved: "Phase III – klinischer Studienzugang",
    typeMono: "Mono-Agonist",
    typeDual: "Dual-Agonist",
    typeTriple: "Triple-Agonist",
    develop: "Eli Lilly (Entwicklung)",
    weeks: "Wo.",
  },
  calc: {
    eyebrow: "Widget",
    h1: "Ihr projizierter Gewichtsverlust.",
    lead: "Bewegen Sie den Regler und sehen Sie in Echtzeit, wie sich Studiendaten der drei Generationen auf Ihr Startgewicht projizieren lassen.",
    weight: "Startgewicht",
    kg: "kg",
    target: "Zielgewicht ≈",
    note: "Hinweis:",
    disclaimer:
      "Projektiert auf Basis von klinischen Phase-II/III-Studiendaten. Individuelle Ergebnisse variieren. Diese Anwendung ersetzt keine medizinische Beratung.",
  },
  safety: {
    eyebrow: "Verbraucherschutz",
    h1: "Kein Fortschritt ohne Sorgfalt.",
    lead: "Retatrutid ist ein potentes Molekül in fortgeschrittener klinischer Prüfung. Bevor Sie es in Erwägung ziehen, sollten Sie den Zugang über klinische Studien und die Risiken kennen.",
    legal: {
      title: "Zugang über klinische Studien",
      body1: "Retatrutid ist als Fertigarzneimittel in Thailand noch nicht regulär zugelassen. Ein sicherer Zugang ist ",
      strong: "ausschließlich über genehmigte klinische Studienprogramme",
      body2:
        " möglich – wie das unserer Partner-Klinik im Rahmen der Thai FDA / Ethik-Kommission. Ein unabhängiger Verkauf oder Import außerhalb eines genehmigten Studienprogramms ist nicht der vorgesehene Weg.",
    },
    warn: {
      title: "Vorsicht: Nicht jede Quelle ist geprüft",
      body1: "Online angebotene Retatrutid-Peptide werden oft als „nur für Forschungszwecke“ verkauft. Sie sind ",
      strong: "nicht Teil eines genehmigten Studienprogramms",
      body2:
        " und daher nicht arzneimittelrechtlich geprüft. Reinheit, Dosierung und Sterilität sind unbekannt. Häufig gefundene Probleme: Endotoxin-Verunreinigungen, falsche Sequenzen, bakterielle Kontamination – mit potenziell schweren gesundheitlichen Folgen.",
    },
    med: {
      title: "Ärztliche Begleitung ist Pflicht",
      body: "Alle Wirkstoffe dieser Klasse erhöhen Herzfrequenz, beeinflussen den Blutzucker und können gastrointestinale Nebenwirkungen auslösen. Blutdruck, Puls, Pankreas-Enzyme und Nierenfunktion müssen regelmäßig überwacht werden – nur durch eine qualifizierte ärztliche Fachkraft.",
    },
    todo: {
      title: "Was Sie tun können",
      items: [
        "Prüfen Sie Ihre Eignung für das genehmigte klinische Studienprogramm unserer Partner-Klinik.",
        "Nutzen Sie zugelassene Alternativen (Semaglutid, Tirzepatid) unter ärztlicher Aufsicht.",
        "Verzichten Sie ausdrücklich auf ungeprüfte Online-Produkte.",
      ],
    },
  },
  notFound: {
    title: "Seite nicht gefunden",
    body: "Die gesuchte Seite existiert nicht oder wurde verschoben.",
    home: "Zur Startseite",
  },
  error: {
    title: "Diese Seite konnte nicht geladen werden",
    body: "Bitte versuchen Sie es erneut oder kehren Sie zur Startseite zurück.",
    retry: "Erneut versuchen",
    home: "Zur Startseite",
  },
};

const en: typeof de = {
  nav: {
    home: "Home",
    science: "Science",
    studies: "Studies",
    calculator: "Calculator",
    safety: "Safety",
    faq: "FAQ",
  },

  common: {
    languageLabel: "Language",
    themeToggle: "Toggle color scheme",
  },
  footer: {
    tagline: "Independent information portal on the triple-agonist Retatrutide.",
    disclaimer:
      "This site is for informational purposes only and does not replace medical advice. As of 2026.",
    linkScience: "Mechanism of Action",
    linkStudies: "Clinical Studies",
    linkCalc: "Weight-Loss Calculator",
    linkSafety: "Safety & Legal",
  },
  home: {
    badge: "Medically supervised · Partner clinic Bangkok",
    h1a: "Retatrutide treatment in",
    h1em: "Thailand",
    h1b: "— medically supervised, legal, personal.",
    lead: "We give you access to the new GLP-1/GIP/glucagon generation through a partner clinic in Bangkok operating under an official study authorisation — including screening, dose escalation and end-to-end English/German-speaking care.",
    ctaConsult: "Free consultation call",
    ctaHow: "How it works",
    ctaData: "View study data",
    ctaCalc: "Calculate weight loss",
    intro: {
      eyebrow: "Introduction",
      title: "Retatrutide: The peptide everyone is talking about right now",
      paragraphs: [
        "Retatrutide, also known as Reta Peptide or LY3437943, is one of the most exciting new active ingredients in weight management, metabolism and modern peptide research. It is particularly interesting because it acts not just on one but on three important signalling pathways in the body: GLP-1, GIP and glucagon. That is why it is called a triple agonist.",
        "Many people are interested in Retatrutide because studies link it to significant weight loss, better appetite control and positive effects on metabolism. While well-known active ingredients such as Semaglutide or Tirzepatide have already received a lot of attention, Retatrutide is considered by many to be the possible next big step in modern weight-loss and metabolic therapies.",
        "The special approach of Retatrutide is that it can influence several processes at the same time. It can support satiety, reduce the feeling of hunger and influence the way the body uses energy. That is exactly why many people search for terms such as Retatrutide effect, Reta peptide, Retatrutide weight loss, Retatrutide slimming, Retatrutide experiences, Retatrutide vs Tirzepatide or Retatrutide vs Semaglutide.",
      ],
    },
    trustRow: {
      a: "Partner clinic with study authorisation",
      b: "Personal care in EN / DE",
      c: "Medical supervision & monitoring",
    },
    how: {
      eyebrow: "Process",
      title: "How your treatment works",
      lead: "Four clear steps — from your first enquiry to ongoing care.",
      steps: [
        {
          title: "1 · Free consultation",
          body: "Non-binding first call (video or phone) in English or German. We answer your questions and outline the possible treatment path.",
        },
        {
          title: "2 · Medical screening",
          body: "History, lab work and BMI assessment by the partner clinic's medical team. You receive a clear yes/no recommendation.",
        },
        {
          title: "3 · Treatment in Bangkok",
          body: "Therapy starts at the clinic: compound administration under the study protocol, controlled dose escalation, personal onboarding.",
        },
        {
          title: "4 · Ongoing care",
          body: "Regular monitoring, refills and physician follow-ups throughout the entire treatment period.",
        },
      ],
    },
    included: {
      eyebrow: "What's included",
      title: "Everything your treatment covers",
      lead: "A package that covers every medically relevant step — listed transparently.",
      items: [
        "Medical screening including lab work",
        "Compound under the approved study protocol",
        "Individual dose escalation",
        "Regular physician appointments & monitoring",
        "English- and German-speaking care",
        "Digital progress documentation",
        "Emergency contact in Bangkok",
        "Optional travel & stay support",
      ],
      priceNote: "Price on request — tailored to treatment duration and dose plan.",
    },
    results: {
      eyebrow: "What to expect",
      title: "Up to −24 % body weight",
      lead: "In the TRIUMPH Phase II trials patients on Retatrutide lost around 24 % of body weight on average after 48 weeks — significantly more than on Semaglutide or Tirzepatide.",
      metricLabel: "Avg. weight loss after 48 weeks (Phase II)",
      disclaimer:
        "Individual results vary. Figures come from clinical trials and do not replace medical advice.",
      ctaStudies: "See the study data",
      ctaCalc: "Project your outcome",
    },
    why: {
      eyebrow: "Why Retatrutid Thailand",
      title: "An offer you can trust",
      cards: [
        {
          title: "Legal framework",
          body: "Treatment only within the approved study framework of our Bangkok partner clinic — no grey or black market.",
        },
        {
          title: "Medical quality",
          body: "Standardised protocol, controlled dose escalation and continuous monitoring by specialist physicians.",
        },
        {
          title: "End-to-end in your language",
          body: "From first enquiry through treatment to follow-up — personal contact in your language.",
        },
      ],
    },
    scienceIntro: {
      eyebrow: "Science & safety",
      title: "Grounded. Transparent. Verifiable.",
      lead: "We work strictly on the basis of published trial data and open communication. Here is the short version — full detail on the subpages.",
    },
    tiles: {
      evoEyebrow: "Evolution",
      evoTitle: "The next evolution",
      evoBody: "Triple-agonist: GLP-1, GIP and glucagon activated at once.",
      triumphEyebrow: "TRIUMPH",
      triumphTitle: "Clinical study results",
      triumphBody: "24% average weight loss after 48 weeks.",
      bgEyebrow: "Biochemistry",
      bgTitle: "Scientific background",
      bgBody: "How three receptors reprogram your metabolism.",
      safeEyebrow: "Law & risk",
      safeTitle: "Safety & legality",
      safeBody: "Regulatory status 2026 and a warning about the black market.",
      benchEyebrow: "Benchmark",
      benchCaption:
        "Average weight loss after 48 weeks on Retatrutide in Phase II. Reference: Semaglutide ~15%, Tirzepatide ~22%.",
      genEyebrow: "Generations",
    },
    faqTeaser: {
      eyebrow: "Common questions",
      title: "Answered briefly",
      cta: "See all questions",
      items: [
        {
          q: "Is the treatment legal?",
          a: "Yes. It takes place at a partner clinic in Bangkok within a study framework approved by the Thai authorities.",
        },
        {
          q: "Do I have to travel to Thailand?",
          a: "For the start and onboarding, yes. Follow-up appointments can partly be handled remotely — we tailor the plan to your situation.",
        },
        {
          q: "How much does treatment cost?",
          a: "The price depends on treatment duration and dose plan. You receive a transparent quote during the consultation call.",
        },
        {
          q: "How quickly will I see results?",
          a: "First effects are typically noticeable within a few weeks. The trial data refer to 48 weeks of treatment.",
        },
      ],
    },
    finalCta: {
      title: "Ready for a non-binding consultation?",
      lead: "We get back to you within 24 hours — personally, discreetly and without sales pressure.",
      reassure: "Free · Non-binding · Confidential",
      cta: "Request a consultation",
    },
  },
  science: {
    eyebrow: "Mechanism of Action",
    h1: "Three receptors. One molecule. A new metabolism.",
    lead: "Retatrutide is the first synthetic peptide that simultaneously modulates three key metabolic receptors. Each contributes distinctly — the combination is more than the sum of its parts.",
    combo: {
      title: "Why the combination matters",
      body: "Mono-agonists like Semaglutide address only the satiety signal. Dual-agonists like Tirzepatide add insulin metabolism. Retatrutide also targets basal energy expenditure — interrupting the metabolic compensation with which the body usually sabotages any diet.",
    },
    m: [
      {
        title: "GLP-1 receptor",
        lead: "Brain satiety, delayed gastric emptying.",
        body: "Binds to receptors in the hypothalamus and amplifies central satiety signals. Gastric transit slows down — portions shrink, satiety lasts longer.",
      },
      {
        title: "GIP receptor",
        lead: "Insulin sensitivity and lipid metabolism.",
        body: "Improves peripheral insulin sensitivity and modulates lipolysis. Fat stores are mobilized more efficiently, postprandial glucose spikes are reduced.",
      },
      {
        title: "Glucagon receptor",
        lead: "Directly raises basal metabolic rate.",
        body: "The decisive novelty: glucagon-receptor activation raises basal energy burn. The body burns more calories at rest — independent of exercise or diet.",
      },
    ],
  },
  studies: {
    eyebrow: "TRIUMPH Program",
    h1: "The data behind the paradigm shift.",
    lead: "Phase II and Phase III trials show a magnitude of effect previously unseen in pharmacological obesity therapy.",
    headline: "Headline result",
    headlineCaption: "Average weight loss after 48 weeks of treatment.",
    duration: "Phase II treatment duration",
    phase: "Ongoing TRIUMPH studies",
    compareTitle: "Three generations compared",
    compareLead: "Compound, mechanism, effect size and regulatory status.",
    th: {
      gen: "Generation",
      drug: "Compound",
      type: "Type",
      loss: "Weight loss",
      status: "Status Thailand",
    },
    approved: "Approved by Thai FDA",
    notApproved: "Phase III – clinical study access",
    typeMono: "Mono-agonist",
    typeDual: "Dual-agonist",
    typeTriple: "Triple-agonist",
    develop: "Eli Lilly (in development)",
    weeks: "wk",
  },
  calc: {
    eyebrow: "Widget",
    h1: "Your projected weight loss.",
    lead: "Move the slider to see, in real time, how study data for the three generations project onto your starting weight.",
    weight: "Starting weight",
    kg: "kg",
    target: "Target weight ≈",
    note: "Note:",
    disclaimer:
      "Projected based on Phase II/III clinical study data. Individual results vary. This tool does not replace medical advice.",
  },
  safety: {
    eyebrow: "Consumer protection",
    h1: "No progress without caution.",
    lead: "Retatrutide is a potent molecule in advanced clinical trials. Before considering it, know the clinical-study access pathway and the risks.",
    legal: {
      title: "Access through clinical studies",
      body1: "Retatrutide is not yet approved as a finished pharmaceutical in Thailand. Safe access is ",
      strong: "only available through approved clinical study programs",
      body2:
        " such as our partner clinic's program under the Thai FDA / ethics committee. Independent sale or import outside an approved study program is not the intended route.",
    },
    warn: {
      title: "Caution: not every source is verified",
      body1: "Retatrutide peptides sold online are often labeled 'for research use only'. They are ",
      strong: "not part of an approved study program",
      body2:
        " and therefore not vetted under pharmaceutical law. Purity, dosage and sterility are unknown. Common issues: endotoxin contamination, incorrect sequences, bacterial contamination — with potentially severe health consequences.",
    },
    med: {
      title: "Medical supervision is mandatory",
      body: "All compounds in this class raise heart rate, affect blood glucose and can trigger gastrointestinal side effects. Blood pressure, pulse, pancreatic enzymes and kidney function must be monitored regularly — only by a qualified medical professional.",
    },
    todo: {
      title: "What you can do",
      items: [
        "Check your eligibility for our partner clinic's approved clinical study program.",
        "Use approved alternatives (Semaglutide, Tirzepatide) under medical supervision.",
        "Explicitly avoid unverified online products.",
      ],
    },
  },
  notFound: {
    title: "Page not found",
    body: "The page you're looking for doesn't exist or has been moved.",
    home: "Go home",
  },
  error: {
    title: "This page failed to load",
    body: "Please try again or return to the home page.",
    retry: "Try again",
    home: "Go home",
  },
};

export const translations = { de, en };
export type Dict = typeof de;
